import { navigateTo, useRoute } from '#imports'
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import { TRACKING_PAGE_TYPE_BY_ROUTE_NAME } from '~/constants/trackingPageType'

import { DASHBOARD } from '../../../routes'

import {
  TRACKING_ZONE,
  ToastName,
  TrackingName,
} from './useFavoritesToast.constant'
import translations from './useFavoritesToast.translations'

export interface FavoritesToastInterface {
  openSavedFavToast(): void
  openAlreadySavedFavToast(): void
  openRemoveFavToast(onUndo: () => void, onClose?: () => void): void
}

/**
 * Creates a favorites toast interface with methods to open and close favorites toasts.
 *
 * @returns Object with favoritesToast and isRemoveFavoriteToastOpen properties
 */
export function useFavoritesToast() {
  const i18n = useI18n()
  const { openSuccessToast, openErrorToast } = useTheToast()
  const tracking = useTracking()
  const route = useRoute()
  const isRemoveFavoriteToastOpen = ref(false)

  const pageType = computed(() => {
    const routeName = String(route.name)

    return TRACKING_PAGE_TYPE_BY_ROUTE_NAME[routeName]
  })

  function trackLinkClick() {
    const name = TrackingName.SeeFavorites

    tracking.trackClick({ name, pageType: pageType.value, zone: TRACKING_ZONE })
  }

  function trackImpression(name: string) {
    tracking.trackToastView({
      name,
      pageType: pageType.value,
      zone: TRACKING_ZONE,
    })
  }

  const favoritesToast: FavoritesToastInterface = {
    openSavedFavToast() {
      const title = i18n(translations.savedFavTitle)
      const content = i18n(translations.savedFavContent)
      const actionLabel = i18n(translations.savedFavActionText)
      const name = ToastName.SavedFavorite

      openSuccessToast({
        title,
        content,
        actionLabel,
        actionCallback: () => {
          trackLinkClick()
          navigateTo({ name: DASHBOARD.FAVORITES })
        },
        name,
      })

      trackImpression(TrackingName.AddedToFavorites)
    },

    openAlreadySavedFavToast() {
      const title = i18n(translations.alreadySavedFavTitle)
      const content = i18n(translations.alreadySavedFavContent)
      const actionLabel = i18n(translations.alreadySavedFavActionText)
      const name = ToastName.AlreadySavedFavorite

      openSuccessToast({
        title,
        content,
        actionLabel,
        actionCallback: () => {
          trackLinkClick()
          navigateTo({ name: DASHBOARD.FAVORITES })
        },
        name,
      })

      trackImpression(TrackingName.AlreadyInFavorites)
    },

    openRemoveFavToast(onUndo: () => void, onClose?: () => void) {
      const title = i18n(translations.removeFavTitle)
      const content = i18n(translations.removeFavContent)
      const actionLabel = i18n(translations.removeFavActionText)
      const name = ToastName.RemoveFavorite

      isRemoveFavoriteToastOpen.value = true

      openErrorToast({
        title,
        content,
        actionLabel,
        actionCallback: () => {
          isRemoveFavoriteToastOpen.value = false
          onUndo()
        },
        closeCallback: () => {
          isRemoveFavoriteToastOpen.value = false
          onClose?.()
        },
        name,
      })
    },
  }

  return {
    favoritesToast,
    isRemoveFavoriteToastOpen,
  }
}
